import { useState } from "react";
import { useTreeLotRelation } from "@/hooks";
import { campaignApi } from "@/lib/api";
import type { LotRelationConfirmPayload } from "@/lib/api";
import {
  LOT_RELATION_METHOD_LABELS,
  LOT_RELATION_OPTIONS,
  LOT_RELATION_RESPONSIBILITY,
  RESPONSIBILITY_NOTE,
  lotReferenceView,
  lotRelationLabel,
} from "@/config/lot-relation-config";

interface Props {
  treeId: number;
}

const fmtM = (v: unknown) => (typeof v === "number" ? `${v.toFixed(1).replace(".", ",")} m` : null);
const fmtDate = (v: string | null) => (v ? new Date(v).toLocaleString("pt-BR") : "—");

/**
 * Onde o exemplar está em relação ao lote: sugestão do sistema (geometria) e
 * valor confirmado por pessoa, lado a lado — nunca misturados.
 */
export default function LotRelationCard({ treeId }: Props) {
  const [version, setVersion] = useState(0);
  const { data, loading, error } = useTreeLotRelation(treeId, version);
  const [open, setOpen] = useState(false);

  if (loading && !data) {
    return (
      <div className="bg-white rounded-card border border-background-200/70 p-5 text-sm text-foreground-500">
        Carregando relação com o lote...
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="bg-white rounded-card border border-background-200/70 p-5 text-sm text-red-700">
        Não foi possível carregar a relação com o lote{error ? `: ${error}` : "."}
      </div>
    );
  }

  const s = data.suggestion;
  const m = (s?.metrics ?? {}) as Record<string, unknown>;
  const official = data.lot_relation;
  const shown = official ?? s?.code ?? null;
  const responsibility = shown ? LOT_RELATION_RESPONSIBILITY[shown] ?? "A confirmar" : "A confirmar";
  const ref = lotReferenceView(s?.reference, data);

  return (
    <div className="bg-white rounded-card border border-background-200/70 p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <h3 className="text-sm font-semibold text-foreground-900 font-heading flex items-center gap-2">
          <i className="ri-home-4-line text-accent-500" aria-hidden="true"></i>
          Relação com o lote
        </h3>
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={!s}
          title={s ? undefined : "Exemplar sem coordenada"}
          className="btn-primary text-xs whitespace-nowrap disabled:opacity-50"
        >
          <i className="ri-check-double-line" aria-hidden="true"></i>
          {official ? "Alterar" : "Confirmar"}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className={`rounded-lg border p-3 ${official ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"}`}>
          <p className="text-[11px] uppercase tracking-wide text-foreground-500">Registro oficial</p>
          {official ? (
            <>
              <p className="text-base font-semibold text-foreground-900 mt-0.5">{lotRelationLabel(official)}</p>
              <p className="text-xs text-foreground-600 mt-1">
                {LOT_RELATION_METHOD_LABELS[data.method ?? ""] ?? data.method} por {data.confirmed_by_name ?? "—"} em{" "}
                {fmtDate(data.confirmed_at)}
              </p>
              {data.notes && <p className="text-xs text-foreground-700 mt-1">Obs.: {data.notes}</p>}
            </>
          ) : (
            <p className="text-sm text-amber-800 mt-0.5">Ainda não confirmado por um técnico.</p>
          )}
        </div>
        <div className="rounded-lg border border-background-200 p-3">
          <p className="text-[11px] uppercase tracking-wide text-foreground-500">Sugestão do sistema</p>
          {s ? (
            <>
              <p className="text-base font-semibold text-foreground-900 mt-0.5">{lotRelationLabel(s.code)}</p>
              <p className="text-xs text-foreground-600 mt-1">{s.reason}</p>
              <p className="text-[11px] text-foreground-500 mt-1">
                Tolerância de {fmtM(s.tolerance_m)} (precisão da posição, mínimo 1,5 m)
              </p>
            </>
          ) : (
            <p className="text-sm text-foreground-500 mt-0.5">Sem coordenada para sugerir.</p>
          )}
        </div>
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mt-4 text-sm">
        <Row label="Responsável pelo manejo" value={responsibility} />
        <Row label="Referência" value={ref.title ?? "Sem lote de referência"} />
        <Row label="Quadra" value={ref.block} mono />
        <Row label="Lote" value={ref.lot} mono />
        <Row label="Inscrição imobiliária" value={ref.registration} mono />
        {ref.adjacent && <Row label="Lote vizinho (divisa)" value={ref.adjacent} mono />}
        <Row label="Logradouro de frente" value={ref.street} />
        <Row label="CEP" value={ref.cep} mono hint={ref.cepNote} />
        <Row label="Distância ao limite do lote" value={fmtM(m.lot_edge_m) ?? "—"} />
        <Row label="Distância à frente do lote" value={fmtM(m.front_edge_m) ?? "—"} />
        <Row label="Lote mais próximo / vizinho" value={fmtM(m.neighbor_lot_m) ?? "—"} />
      </dl>
      <p className="text-[11px] text-foreground-500 mt-3">{RESPONSIBILITY_NOTE}</p>

      {open && s && (
        <ConfirmModal
          treeId={treeId}
          suggestion={s.code}
          candidates={s.candidates}
          current={official}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            setVersion((v) => v + 1);
          }}
        />
      )}
    </div>
  );
}

function Row({ label, value, mono, hint }: { label: string; value: string; mono?: boolean; hint?: string | null }) {
  return (
    <div>
      <dt className="text-[11px] text-foreground-500 uppercase tracking-wide">{label}</dt>
      <dd className={`text-foreground-900 font-medium ${mono ? "font-mono" : ""}`}>{value}</dd>
      {hint && <dd className="text-[11px] text-foreground-500">{hint}</dd>}
    </div>
  );
}

function ConfirmModal({
  treeId,
  suggestion,
  candidates,
  current,
  onClose,
  onSaved,
}: {
  treeId: number;
  suggestion: string;
  candidates: string[];
  current: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const initial = current ?? (suggestion !== "INDETERMINADO" ? suggestion : "");
  const [relation, setRelation] = useState(initial);
  const [method, setMethod] = useState<LotRelationConfirmPayload["method"]>("CONFIRMADO_CAMPO");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Mesma regra do servidor: divergir da sugestão exige o motivo.
  const diverges =
    !!relation && (!candidates.includes(relation) || (suggestion !== "INDETERMINADO" && relation !== suggestion));
  const noteOk = !diverges || notes.trim().length >= 10;

  async function save() {
    if (!relation) return;
    setSaving(true);
    setErr(null);
    try {
      await campaignApi.confirmLotRelation(treeId, {
        lot_relation: relation,
        method,
        notes: notes.trim() || undefined,
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-card w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
        <h3 className="text-base font-semibold text-foreground-900 mb-1">Relação com o lote</h3>
        <p className="text-xs text-foreground-500 mb-4">
          Sugestão do sistema: <strong>{lotRelationLabel(suggestion)}</strong>. A decisão é do técnico e fica
          registrada no histórico do exemplar.
        </p>

        <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
          <legend className="text-xs font-medium text-foreground-700 mb-1">Onde está o exemplar</legend>
          {LOT_RELATION_OPTIONS.map((code) => (
            <label
              key={code}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer ${
                relation === code ? "border-primary-300 bg-primary-50 text-primary-800" : "border-background-200"
              }`}
            >
              <input type="radio" name="lot-relation" value={code} checked={relation === code} onChange={() => setRelation(code)} />
              <span>
                {lotRelationLabel(code)}
                {candidates.includes(code) && <span className="text-[10px] text-foreground-500"> · provável</span>}
              </span>
            </label>
          ))}
        </fieldset>

        <fieldset className="mb-4">
          <legend className="text-xs font-medium text-foreground-700 mb-1">Como foi verificado</legend>
          <div className="flex flex-wrap gap-2">
            {(["CONFIRMADO_CAMPO", "INFORMADO_ESCRITORIO"] as const).map((code) => (
              <label
                key={code}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm cursor-pointer ${
                  method === code ? "border-primary-300 bg-primary-50" : "border-background-200"
                }`}
              >
                <input type="radio" name="lot-method" checked={method === code} onChange={() => setMethod(code)} />
                {LOT_RELATION_METHOD_LABELS[code]}
              </label>
            ))}
          </div>
        </fieldset>

        <label className="block mb-2">
          <span className="text-xs font-medium text-foreground-700">
            Observação{diverges ? " (obrigatória: a escolha difere da sugestão)" : ""}
          </span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder="Ex.: muro recuado, a árvore está dentro do terreno."
            className="mt-1 w-full px-3 py-2 rounded-lg border border-background-200 text-sm"
          />
        </label>
        {diverges && !noteOk && <p className="text-xs text-amber-700 mb-2">Descreva o motivo (mínimo de 10 caracteres).</p>}
        {err && (
          <p className="text-xs text-red-700 mb-2" role="alert">
            {err}
          </p>
        )}

        <div className="flex justify-end gap-2 mt-3">
          <button type="button" onClick={onClose} className="btn-outline text-sm">
            Cancelar
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!relation || !noteOk || saving}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {saving ? "Salvando..." : "Registrar"}
          </button>
        </div>
      </div>
    </div>
  );
}
